<template>
  <div class="data-viewer" :class="{ 'is-grid': isGrid, 'is-list': isList }">
    <template v-if="isGrid">
      <div v-for="item in sortedItems" class="grid-cell-container">
        <div
          class="grid-cell"
          :class="{ 'is-clickable': !!$listeners['click:grid-item'] }"
          @click="$emit('click:grid-item', item)"
        >
          <div class="grid-title">
            <slot name="grid.title" :value="item[titleKey]" :item="item">
              <span>{{ item[titleKey] | shortifyText(60) }}</span>
            </slot>
          </div>
          <div class="buttons is-right">
            <slot v-if="hasDuplicate" name="duplicate" :item="item">
              <button
                class="button is-text is-icon"
                @click.stop="$emit('click:duplicate', item)"
              >
                <span class="icon icon-duplicate"></span>
              </button>
            </slot>
            <slot v-if="hasStarred" name="starred" :item="item">
              <button
                class="button is-text is-icon"
                @click.stop="$emit('click:starred', item)"
              >
                <span class="icon icon-starred"></span>
              </button>
            </slot>
            <slot v-if="gridHasEdit" name="grid.edit" :item="item">
              <button
                class="button is-text is-icon"
                @click.stop="$emit('click:grid-edit', item)"
              >
                <span class="icon icon-edit"></span>
              </button>
            </slot>
            <slot v-if="gridHasDelete" name="grid.delete" :item="item">
              <button
                class="button is-text is-icon"
                @click.stop="$emit('click:grid-delete', item)"
              >
                <span class="icon icon-delete"></span>
              </button>
            </slot>
            <slot v-if="gridHasDownload" name="grid.download" :item="item">
              <button
                class="button is-text is-icon"
                @click.stop="$emit('click:grid-download', item)"
              >
                <span class="icon icon-download"></span>
              </button>
            </slot>
            <slot v-if="gridHasExpand" name="grid.expand" :item="item">
              <button
                class="button is-text is-icon"
                @click.stop="$emit('click:grid-expand', item)"
              >
                <span class="icon icon-expand"></span>
              </button>
            </slot>
          </div>
          <div class="grid-subtitle">
            <slot name="grid.subtitle" v-bind="item" :item="item"></slot>
          </div>
          <div class="grid-team">
            <slot name="grid.team" :item="item" :value="item[teamKey]">
              <div class="columns is-vcentered">
                <div class="column is-4">
                  <span class="grid-stat-label">Team</span>
                </div>
                <div ref="team-wrapper" class="column is-8">
                  <div class="team-wrapper">
                    <div
                      v-for="user in teamSlice(item.users)"
                      class="avatar-col"
                    >
                      <user-avatar
                        v-if="user != null"
                        :name="user.__name"
                        :photo="user.photo || ''"
                      ></user-avatar>
                    </div>
                  </div>
                </div>
              </div>
            </slot>
          </div>
          <div class="buttons is-right stop-container">
            <slot v-if="gridHasStop" name="grid.stop" :item="item">
              <button
                class="button is-text is-icon"
                @click.stop="$emit('click:grid-stop', item)"
              >
                <span class="icon icon-stop"></span>
              </button>
            </slot>
          </div>
        </div>
      </div>
    </template>
    <template v-else-if="isList && sortedItems.length > 0">
      <app-table
        :items="sortedItems"
        :headers="headers"
        :sort.sync="sort"
        @click:row="$emit('click:list-row', $event)"
      >
        <template v-for="(_, slot) of listSlots" v-slot:[slot]="scope">
          <slot :name="`list.${slot}`" v-bind="scope"></slot>
        </template>
        <template v-slot:item.team="{ item }">
          <div class="team-wrapper">
            <div v-for="user in teamSlice(item.users)" class="avatar-col">
              <user-avatar
                v-if="user != null"
                :name="user.__name"
                :photo="user.photo || ''"
              ></user-avatar>
            </div>
          </div>
        </template>
      </app-table>
    </template>

    <div v-if="!suppressLoadingEmpty" class="full-width">
      <list-loading :loading="loading" :is-empty="isEmpty"></list-loading>
    </div>
  </div>
</template>

<script>
import AppTable from '@/components/AppTable.vue';
import UserAvatar from '@/components/UserAvatar.vue';
import ListLoading from '@/components/ListLoading.vue';
import _ from 'lodash';

export default {
  name: 'AppDataViewer',
  components: { AppTable, UserAvatar, ListLoading },
  props: {
    loading: {
      type: Boolean,
      default: true
    },
    items: {
      type: Array,
      required: true
    },
    viewBy: {
      type: String,
      default: 'grid',
      validator: function(value) {
        // The value must match one of these strings
        return ['list', 'grid'].indexOf(value) !== -1;
      }
    },
    orderBy: {
      type: Object,
      default: () => {}
    },
    titleKey: {
      type: String,
      default: 'name'
    },
    teamKey: {
      type: String,
      default: 'users'
    },
    headers: {
      type: Array,
      required: true
    },
    hasDuplicate: {
      type: Boolean,
      default: false
    },
    hasStarred: {
      type: Boolean,
      default: false
    },
    gridHasEdit: {
      type: Boolean,
      default: false
    },
    gridHasDelete: {
      type: Boolean,
      default: false
    },
    gridHasDownload: {
      type: Boolean,
      default: false
    },
    gridHasExpand: {
      type: Boolean,
      default: false
    },
    gridHasStop: {
      type: Boolean,
      default: false
    },
    suppressLoadingEmpty: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      sort: {}
    };
  },
  computed: {
    isEmpty() {
      if (!this.loading && this.items.length == 0) return true;
      else return false;
    },
    isGrid() {
      return this.viewBy == 'grid';
    },
    isList() {
      return this.viewBy == 'list';
    },
    listSlots() {
      return _.chain(this.$scopedSlots)
        .pickBy((value, key) => _.startsWith(key, 'list.'))
        .mapKeys((value, key) => key.replace('list.', ''))
        .value();
    },
    sortedItems() {
      const orderFields = _.pickBy(this.orderBy, el => el != 0);
      let orderedItems = _.cloneDeep(this.items);
      for (const key in orderFields) {
        const header = _.find(this.headers, { value: key });
        if (header.customSort !== undefined) {
          orderedItems = orderedItems.sort(header.customSort);
          if (orderFields[key] == -1) {
            orderedItems = orderedItems.reverse();
          }
        } else {
          orderedItems = _.orderBy(
            orderedItems,
            key,
            orderFields[key] == 1 ? 'asc' : 'desc'
          );
        }
      }
      return orderedItems;
    }
  },
  watch: {
    orderBy: {
      deep: true,
      handler(value) {
        if (!_.isEqual(this.sort, this.orderBy)) {
          this.sort = Object.assign({}, this.orderBy);
        }
      }
    },
    sort: {
      deep: true,
      handler(value) {
        if (!_.isEqual(this.sort, this.orderBy)) {
          this.$emit('update:orderBy', value);
        }
      }
    }
  },
  methods: {
    teamSlice(users) {
      if (!users) {
        return [];
      }
      const maxUsers = this.isGrid ? 4 : 6;
      let items = [];
      if (users.length > maxUsers) {
        items = users.slice(0, maxUsers - 1);
        items.push({
          __name: `+${users.slice(maxUsers - 1).length}`,
          photo: ''
        });
      } else {
        items = users;
      }
      return items;
    }
  }
};
</script>

<style lang="scss">
.data-viewer {
  &.is-grid {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -1.5rem;
  }

  .full-width {
    width: 100%;
  }

  .grid-cell-container {
    width: percentage(1/2);
    padding: 1.5rem;
    @include from($widescreen) {
      width: percentage(1/3);
    }
    @include from($fullhd) {
      width: percentage(1/4);
    }
  }

  .grid-cell {
    border-radius: $radius;
    padding: 2.5rem 2rem 4rem;
    height: 100%;
    overflow: hidden;
    position: relative;
    &.is-clickable {
      cursor: pointer;
      transition: all 300ms ease;
      &:hover {
        opacity: 0.8;
      }
    }

    .grid-title {
      font-size: 2rem;
      font-weight: bold;
      height: 70px;
      width: 70%;
      position: relative;
      overflow: hidden;
      z-index: 5;
      //word-break: break-all;
    }
    .grid-subtitle {
      padding-bottom: 3rem;
      position: relative;
      z-index: 6;
    }

    .buttons {
      position: absolute;
      right: 10px;
      top: 30px;
      margin-left: auto;
      min-width: 120px;
      z-index: 7;
      .button {
        margin-left: 10px !important;
      }
    }

    .grid-stat-label {
      font-size: 1.6rem;
      padding-right: 0.4rem;
    }
    .grid-stat-value {
      color: $white;
      font-size: 1.6rem;
    }
  }
  &.is-list {
    .team-wrapper {
      grid-template-columns: repeat(6, 1fr);
    }
  }
  .team-wrapper {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: 65px;
    grid-auto-rows: 0; /* set height to 0 for autogenerated grid rows */
    overflow: hidden; /* hide grid items that overflow */
    .avatar-col {
      position: relative;
      &:not(:first-child) {
        .avatar {
          right: 0;
        }
      }
    }
    .avatar {
      position: absolute;
    }
  }
}
</style>
